import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Manual de uso para el rol admin_tenant — la persona designada por la
 * alcaldía para administrar MiPueblo del lado del cliente.
 *
 * Asume lector NO técnico. Cubre todo lo que ve en el sidebar de su panel:
 * funcionarios, equipos, plantillas, KB, horarios, permisos, SLA,
 * encuestas, métricas, auditoría y Habeas Data.
 */
export function DocsAdminTenantPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Rol admin_tenant
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Manual del administrador de alcaldía
        </h1>

        <div className="callout">
          <strong>Tiempo de lectura:</strong> 25-35 min. Léelo completo la
          primera semana — luego sirve como referencia cuando necesites
          recordar cómo hacer algo puntual.
        </div>

        {/* ---- 1. Tu rol ---- */}
        <Section title="1 — Tu rol y responsabilidades">
          <p>
            Eres <code>admin_tenant</code>: el rol más alto dentro de tu
            alcaldía en MiPueblo. Te lo asignó el equipo MiPueblo durante el
            onboarding y eres el único que puede tocar la configuración
            estructural del sistema.
          </p>
          <p>
            <strong>QUÉ eres:</strong>
          </p>
          <Ul
            items={[
              "Responsable de mantener actualizado el equipo de funcionarios",
              "Quien define dependencias, SLA, horarios y plantillas",
              "Quien revisa la bitácora de auditoría y ejecuta supresiones de Habeas Data",
              "Punto de contacto institucional con el equipo MiPueblo",
            ]}
          />
          <p style={{ marginTop: 14 }}>
            <strong>QUÉ NO eres:</strong>
          </p>
          <Ul
            items={[
              <>
                <strong>No eres super_admin</strong>: no puedes cambiar el NIT,
                slug o datos legales del tenant — eso lo hace el equipo
                MiPueblo desde su panel.
              </>,
              <>
                <strong>No eres operador</strong>: no estás obligado a atender
                chats ni responder PQRSDFs (aunque puedes hacerlo si quieres).
                Por defecto tu casilla <code>puede_chat</code> viene apagada.
              </>,
              <>
                <strong>No puedes archivar tu propia cuenta</strong>: si
                necesitas salir, primero crea otro admin que tome el relevo.
              </>,
            ]}
          />
          <div className="callout warn">
            Recuerda: todo lo que hagas queda registrado en{" "}
            <code>Auditoría</code> con tu nombre, hora y dirección IP.
          </div>
        </Section>

        {/* ---- 2. Acceder ---- */}
        <Section title="2 — Acceder al sistema (primer login)">
          <Ol>
            <li>
              Abre <code>{`{slug}.mipuebloapp.com`}</code> en tu navegador —
              el slug te lo dio el equipo MiPueblo (ej.{" "}
              <code>cajica.mipuebloapp.com</code>).
            </li>
            <li>
              Usa el <strong>email institucional</strong> y la{" "}
              <strong>contraseña temporal</strong> que te enviaron por canal
              seguro.
            </li>
            <li>
              El sistema te <strong>obliga</strong> a cambiar la contraseña en
              el primer ingreso. Elige una de mínimo 8 caracteres con letras,
              números y un símbolo.
            </li>
            <li>
              Después del cambio caes en el panel principal con el sidebar
              completo a la izquierda.
            </li>
          </Ol>
          <div className="callout tip">
            💡 <strong>Tip:</strong> guarda{" "}
            <code>{`{slug}.mipuebloapp.com`}</code> como marcador en tu
            navegador. Nunca uses la misma contraseña que tienes en otros
            sistemas de la alcaldía.
          </div>
        </Section>

        {/* ---- 3. Funcionarios ---- */}
        <Section title="3 — Gestionar funcionarios">
          <p>
            Menú <Kbd>Funcionarios</Kbd> en el sidebar. Aquí das de alta a
            todo el personal de la alcaldía que va a usar MiPueblo.
          </p>

          <h4 style={{ marginTop: 18 }}>Crear un funcionario</h4>
          <Ol>
            <li>
              Click <Kbd>+ Nuevo funcionario</Kbd> arriba a la derecha.
            </li>
            <li>
              Llena los campos básicos:
              <ul>
                <li>
                  <strong>Nombre completo</strong> (como va a verlo el
                  ciudadano)
                </li>
                <li>
                  <strong>Email institucional</strong> (será su login). Evita
                  gmail/hotmail.
                </li>
                <li>
                  <strong>Rol en MiPueblo</strong> — ver tabla abajo
                </li>
                <li>
                  <strong>Dependencia</strong> a la que pertenece
                </li>
              </ul>
            </li>
            <li>
              Datos opcionales pero recomendados para cumplir MIPG:
              identificación (CC), cargo, nivel jerárquico, tipo de
              vinculación, fecha de posesión y acto administrativo.
            </li>
            <li>
              Deja marcado <Kbd>Generar password aleatorio</Kbd> (recomendado).
            </li>
            <li>
              Click <Kbd>Crear</Kbd>. El sistema muestra una vez la{" "}
              <strong>contraseña temporal</strong> — cópiala YA y entrégasela
              al funcionario por canal seguro.
            </li>
          </Ol>

          <h4 style={{ marginTop: 18 }}>Roles disponibles</h4>
          <Table
            head={["Rol", "Qué puede hacer", "Típicamente"]}
            rows={[
              [
                "admin_tenant",
                "Todo el sidebar: configura, audita, gestiona equipo",
                "Secretario TIC o jefe de planeación",
              ],
              [
                "supervisor",
                "Ve todos los radicados, gestiona equipo (no configura), revisa KB",
                "Jefes de dependencia o coordinadores",
              ],
              [
                "funcionario",
                "Atiende radicados de su dependencia y chat ciudadano",
                "Personal operativo de cada secretaría",
              ],
              [
                "radicador",
                "Solo radica nuevos PQRSDFs en ventanilla; no responde",
                "Recepción / ventanilla única",
              ],
            ]}
          />

          <h4 style={{ marginTop: 18 }}>Editar, desactivar, archivar</h4>
          <p>En cada fila de la tabla tienes botones con icono:</p>
          <Ul
            items={[
              <>
                <strong>Lápiz</strong>: editar datos del funcionario (incluye
                cambiar de rol o dependencia)
              </>,
              <>
                <strong>Pausa / Play</strong>: desactivar temporalmente (no
                puede entrar, pero no se borra). Útil para vacaciones o
                licencias.
              </>,
              <>
                <strong>Archivar</strong> (caja): para personas que ya no
                trabajan en la alcaldía. Quedan ocultas del listado pero el
                histórico de quién hizo qué se conserva por auditoría.
              </>,
            ]}
          />
          <p>
            Para ver archivados, marca arriba la casilla{" "}
            <Kbd>Ver archivados</Kbd>. Desde ahí puedes restaurarlos si
            volvieron a la alcaldía.
          </p>

          <div className="callout warn">
            <strong>No puedes desactivar ni archivar tu propia cuenta.</strong>{" "}
            Si necesitas dejar el cargo, primero crea otro <code>admin_tenant</code>{" "}
            y pídele que te archive a ti.
          </div>
        </Section>

        {/* ---- 4. Dependencias ---- */}
        <Section title="4 — Dependencias y equipos">
          <p>
            Las <strong>dependencias</strong> son las secretarías y oficinas
            de tu alcaldía (Gobierno, Salud, Hacienda, Movilidad, Planeación,
            etc.). Cada radicado se enruta a una dependencia, y los
            funcionarios pertenecen a una.
          </p>
          <p>
            Las dependencias iniciales las crea el equipo MiPueblo durante el
            onboarding. Para agregar más, ve a <Kbd>Datos de la alcaldía</Kbd>{" "}
            o pide a soporte que las cree (afectan estructura organizacional y
            requieren validación).
          </p>

          <h4 style={{ marginTop: 18 }}>Equipos (grupos de funcionarios)</h4>
          <p>
            Menú <Kbd>Equipos</Kbd>. Un equipo es un grupo de funcionarios
            que comparte una bandeja, útil para repartir trabajo dentro de
            una misma dependencia o entre varias.
          </p>
          <Ol>
            <li>
              Click <Kbd>+ Nuevo equipo</Kbd>
            </li>
            <li>
              Dale un <strong>nombre</strong> (ej. &quot;Mesa de ayuda
              ciudadana&quot;)
            </li>
            <li>
              Agrega <strong>miembros</strong> de cualquier dependencia
            </li>
            <li>
              Opcional: asóciale una <strong>dependencia principal</strong>
            </li>
          </Ol>
          <p>
            Los equipos se usan en <code>Auto-asignación</code> para enrutar
            radicados a grupos en lugar de personas individuales.
          </p>
        </Section>

        {/* ---- 5. Plantillas ---- */}
        <Section title="5 — Plantillas de respuesta">
          <p>
            Menú <Kbd>Plantillas</Kbd>. Son mensajes pre-redactados que los
            funcionarios insertan al responder un PQRSDF, para ahorrar tiempo
            y mantener tono uniforme.
          </p>
          <p>
            El tenant arranca con <strong>plantillas por defecto</strong> ya
            sembradas (acuse de recibo, solicitud de información,
            traslado por competencia, respuesta de fondo). Tu trabajo es
            adaptarlas al lenguaje de tu alcaldía.
          </p>

          <h4 style={{ marginTop: 18 }}>Editar una plantilla existente</h4>
          <Ol>
            <li>
              Click en el <strong>título</strong> de la plantilla en la lista
            </li>
            <li>
              Edita <strong>título</strong>, <strong>tipo aplicable</strong>{" "}
              (petición / queja / reclamo / etc. o &quot;todos&quot;) y{" "}
              <strong>contenido</strong>
            </li>
            <li>
              Puedes usar <strong>variables</strong> como{" "}
              <code>{`{{ciudadano.nombre}}`}</code>,{" "}
              <code>{`{{radicado.numero}}`}</code>,{" "}
              <code>{`{{alcaldia.nombre}}`}</code> — se reemplazan al enviar
            </li>
            <li>
              <Kbd>Guardar</Kbd>. Cada cambio crea una nueva{" "}
              <strong>versión</strong> — puedes ver el historial desde el
              ícono de reloj.
            </li>
          </Ol>

          <h4 style={{ marginTop: 18 }}>Crear plantilla nueva</h4>
          <p>
            Click <Kbd>+ Nueva plantilla</Kbd>. Mismos campos que editar.
            Pensar antes:
          </p>
          <Ul
            items={[
              "¿Aplica solo a un tipo (ej. solo quejas) o a todos?",
              "¿Es para acuse, pedir info, trasladar o respuesta final?",
              "Lenguaje claro, sin tecnicismos, dirigido al ciudadano",
              "Cita la norma cuando aplique (Ley 1755/2015)",
            ]}
          />

          <div className="callout tip">
            💡 Las plantillas no son obligatorias — el funcionario siempre
            puede escribir respuesta libre. Sirven para los casos repetitivos.
          </div>
        </Section>

        {/* ---- 6. Centro de ayuda ---- */}
        <Section title="6 — Centro de ayuda (KB)">
          <p>
            Menú <Kbd>Centro de ayuda</Kbd>. Es la base de conocimiento
            pública de tu alcaldía. <strong>Es lo más importante que vas a
            mantener</strong> porque:
          </p>
          <Ul
            items={[
              "Resuelve consultas comunes 24/7 sin necesidad de operador",
              "Alimenta al bot de chat (si está activado) para responder en lenguaje natural",
              <>
                Es público en{" "}
                <code>{`{slug}.mipuebloapp.com/ayuda`}</code> — los ciudadanos
                lo consultan directo
              </>,
              "Reduce drásticamente el volumen de PQRSDF si está bien sembrado",
            ]}
          />

          <h4 style={{ marginTop: 18 }}>Crear un artículo</h4>
          <Ol>
            <li>
              Click <Kbd>+ Nuevo artículo</Kbd>
            </li>
            <li>
              <strong>Título</strong>: como lo buscaría el ciudadano (ej.
              &quot;¿Cómo pago el impuesto predial?&quot;)
            </li>
            <li>
              <strong>Resumen</strong> de 1-2 líneas (sale en el listado y
              en el chat)
            </li>
            <li>
              <strong>Contenido completo</strong>: pasos, requisitos,
              horarios, costos, contactos. Usa lenguaje ciudadano, no
              jurídico.
            </li>
            <li>
              <strong>Categoría</strong>: agrupa por tema (Impuestos, Salud,
              Trámites, etc.)
            </li>
            <li>
              Marca <Kbd>Publicado</Kbd>. Si lo dejas como borrador, no sale
              al público ni alimenta al bot.
            </li>
          </Ol>

          <h4 style={{ marginTop: 18 }}>Qué sembrar primero</h4>
          <p>
            Empieza por los <strong>10-15 trámites más comunes</strong> del
            municipio. Pregúntale a tu equipo de atención al ciudadano cuáles
            son las preguntas que más reciben. Ejemplos típicos:
          </p>
          <Ul
            items={[
              "Pago de impuesto predial e industria y comercio",
              "Certificados (residencia, estratificación, paz y salvo)",
              "Horarios de oficinas y secretarías",
              "Recolección de basuras / horarios",
              "Consulta de comparendos",
              "Trámite de licencias de construcción",
              "Subsidios sociales (Familias en Acción, adulto mayor)",
              "Cita con el alcalde o secretarios",
              "Cómo radicar una PQRSDF",
              "Líneas de emergencia",
            ]}
          />

          <div className="callout tip">
            💡 Si la alcaldía ya tiene un sitio web institucional con esta
            info, puedes copy-paste para acelerar. Revisa periódicamente que
            los datos sigan vigentes (precios, horarios cambian).
          </div>
        </Section>

        {/* ---- 7. Horarios ---- */}
        <Section title="7 — Horarios y festivos">
          <p>
            Menú <Kbd>Horarios</Kbd>. Define cuándo está abierta tu alcaldía.
            Esto afecta:
          </p>
          <Ul
            items={[
              "El cálculo de plazos SLA (solo cuentan horas hábiles si lo activas)",
              "El mensaje de fuera de horario en chat ciudadano",
              "La auto-asignación de operadores",
            ]}
          />

          <h4 style={{ marginTop: 18 }}>Configurar la semana</h4>
          <Ol>
            <li>
              Para cada día (lunes a domingo), marca si está{" "}
              <strong>abierto</strong>
            </li>
            <li>
              Agrega uno o más <strong>tramos horarios</strong> — ej.{" "}
              <code>08:00 - 12:00</code> y <code>14:00 - 17:30</code> para
              respetar el almuerzo
            </li>
            <li>
              Para viernes corto, ajusta el segundo tramo (ej.{" "}
              <code>14:00 - 16:00</code>)
            </li>
            <li>
              <Kbd>Guardar</Kbd>
            </li>
          </Ol>

          <h4 style={{ marginTop: 18 }}>Festivos</h4>
          <p>
            Los <strong>festivos oficiales de Colombia</strong> están
            pre-cargados y se actualizan año a año automáticamente. No tienes
            que tocarlos. El sistema los considera como días no hábiles para
            el cálculo SLA.
          </p>
        </Section>

        {/* ---- 8. Permisos ---- */}
        <Section title="8 — Permisos por dependencia">
          <p>
            Menú <Kbd>Permisos</Kbd>. Una sola decisión clave:{" "}
            <strong>¿los funcionarios ven todos los radicados de la
            alcaldía, o solo los de su dependencia?</strong>
          </p>

          <Table
            head={["Modo", "Quién ve qué", "Cuándo usar"]}
            rows={[
              [
                "Abierto (default)",
                "Todo funcionario ve todos los radicados de toda la alcaldía",
                "Alcaldías pequeñas o equipos muy colaborativos",
              ],
              [
                "Por dependencia",
                "Funcionario solo ve radicados de SU dependencia. Supervisor y admin ven todo.",
                "Alcaldías medianas / grandes con secretarías independientes",
              ],
            ]}
          />

          <p>
            Para activar el modo restringido, marca{" "}
            <Kbd>Scope por dependencia</Kbd> y guarda.
          </p>

          <div className="callout warn">
            El admin y supervisores <strong>siempre</strong> ven todo,
            independiente de este ajuste.
          </div>
        </Section>

        {/* ---- 9. SLA ---- */}
        <Section title="9 — SLA y plazos legales">
          <p>
            Menú <Kbd>SLA</Kbd>. Aquí defines cuántos días hábiles tiene tu
            alcaldía para responder cada tipo de PQRSDF según su prioridad.
          </p>

          <h4 style={{ marginTop: 18 }}>Valores por defecto (Ley 1755/2015)</h4>
          <Table
            head={["Tipo", "Días hábiles"]}
            rows={[
              ["Petición de información", "10"],
              ["Petición general", "15"],
              ["Queja / Reclamo", "15"],
              ["Petición entre autoridades", "10"],
              ["Consultas / conceptos", "30"],
            ]}
          />

          <p>
            La tabla del panel cruza <strong>tipo</strong> con{" "}
            <strong>prioridad</strong> (baja, normal, alta, urgente) para
            darte más granularidad.
          </p>

          <h4 style={{ marginTop: 18 }}>Editar SLA</h4>
          <Ol>
            <li>
              Cambia los días en cada celda. Recomendación: deja los valores
              que cumplen la ley o más exigentes — nunca más laxos.
            </li>
            <li>
              Marca <Kbd>Solo horario hábil</Kbd> si quieres que los plazos
              cuenten solo tiempo dentro de tus horarios de atención (más
              estricto pero más justo).
            </li>
            <li>
              <Kbd>Guardar</Kbd>. Aplica para nuevos radicados; los antiguos
              mantienen el SLA con que se crearon.
            </li>
          </Ol>

          <div className="callout warn">
            <strong>Importante legal:</strong> Ley 1755/2015 fija mínimos. NO
            puedes ponerle más días de los que dice la ley sin incumplir.
            Puedes ponerle menos (más estricto).
          </div>
        </Section>

        {/* ---- 10. Encuestas ---- */}
        <Section title="10 — Encuestas de satisfacción">
          <p>
            Menú <Kbd>Encuestas</Kbd>. Al cerrar un radicado el ciudadano
            recibe una encuesta. Por defecto trae una pregunta de
            calificación 1-5 estrellas — tú puedes agregar hasta el máximo
            permitido.
          </p>

          <h4 style={{ marginTop: 18 }}>Agregar preguntas</h4>
          <Ol>
            <li>
              Elige el tipo:
              <ul>
                <li>
                  <strong>Opción múltiple</strong> — para clasificar
                  (ej. &quot;¿En qué dependencia te atendieron?&quot;)
                </li>
                <li>
                  <strong>Texto libre</strong> — para comentarios abiertos
                </li>
                <li>
                  <strong>Sí / No</strong> — preguntas binarias rápidas
                </li>
              </ul>
            </li>
            <li>
              Escribe el <strong>label</strong> (la pregunta como la ve el
              ciudadano)
            </li>
            <li>
              Si es opción múltiple, agrega las opciones una por una
            </li>
            <li>
              Marca <strong>obligatoria</strong> si no quieres permitir
              saltarla
            </li>
            <li>
              Usa las flechas para reordenar. <Kbd>Guardar</Kbd>.
            </li>
          </Ol>

          <div className="callout tip">
            💡 Mantén la encuesta CORTA (3-5 preguntas máximo). Encuestas
            largas tienen tasas de respuesta muy bajas.
          </div>
        </Section>

        {/* ---- 11. Métricas ---- */}
        <Section title="11 — Métricas e informes">
          <p>
            Tienes dos vistas de métricas:
          </p>

          <h4 style={{ marginTop: 18 }}>Panel principal (PQRSDF)</h4>
          <p>
            Menú <Kbd>Panel</Kbd>. Volumen de radicados, % cumplido vs
            vencido, distribución por tipo / dependencia / canal.
            Filtrable por rango de fechas.
          </p>

          <h4 style={{ marginTop: 18 }}>Métricas del chat ciudadano</h4>
          <p>
            Desde <Kbd>Chat ciudadano</Kbd> → botón <Kbd>Métricas</Kbd>{" "}
            arriba. Te dice qué tan bien resuelve tu equipo ANTES de que la
            consulta se convierta en PQRSDF. Mide:
          </p>
          <Ul
            items={[
              "Total de chats recibidos en el rango",
              "% resueltos sin PQRSDF (alto = bueno)",
              "Tiempo promedio de primera respuesta",
              "Tiempo promedio de resolución",
              "% atendidos por bot vs operador",
            ]}
          />

          <div className="callout tip">
            💡 Si el % resuelto sin PQRSDF está bajo (&lt;40%), revisa: ¿el
            KB tiene suficientes artículos? ¿el bot está activo y bien
            configurado? ¿los operadores conocen las plantillas?
          </div>
        </Section>

        {/* ---- 12. Auditoría ---- */}
        <Section title="12 — Auditoría (bitácora)">
          <p>
            Menú <Kbd>Auditoría</Kbd>. Registro inmutable de TODO lo que
            ocurre en tu alcaldía. Incluye:
          </p>
          <Ul
            items={[
              "Logins de funcionarios (quién y desde qué IP)",
              "Cambios de contraseña",
              "Creación, edición y archivo de funcionarios",
              "Cambios de configuración (horarios, SLA, plantillas, KB)",
              "Respuestas enviadas a ciudadanos",
              <>
                <strong>Accesos del equipo MiPueblo en modo soporte</strong>{" "}
                (marcados con ⚠) — para auditarnos a nosotros también
              </>,
              "Cambios en credenciales de WhatsApp u otros canales",
            ]}
          />

          <p>
            Cada entrada muestra: acción, quién, cuándo, IP, y datos
            relevantes. La bitácora <strong>no se puede editar ni borrar</strong>{" "}
            — es prueba para auditorías externas (Contraloría, control
            interno).
          </p>

          <div className="callout tip">
            💡 Revisa la bitácora al menos una vez por semana. Busca patrones
            raros: logins a horas extrañas, muchos intentos fallidos, cambios
            de configuración no autorizados.
          </div>
        </Section>

        {/* ---- 13. Habeas Data ---- */}
        <Section title="13 — Habeas Data (supresión de datos)">
          <p>
            Menú <Kbd>Habeas Data</Kbd>. Cuando un ciudadano pide el borrado
            de sus datos personales (Ley 1581/2012), la solicitud aparece
            aquí.
          </p>

          <h4 style={{ marginTop: 18 }}>Plazo legal</h4>
          <p>
            Tienes <strong>10 días hábiles</strong> desde que entra la
            solicitud para ejecutarla. Pasado ese plazo, la SIC puede
            sancionar a la alcaldía.
          </p>

          <h4 style={{ marginTop: 18 }}>Cómo ejecutar la supresión</h4>
          <Ol>
            <li>
              Verifica la identidad del solicitante (que sea el titular o su
              representante legal autorizado)
            </li>
            <li>
              Click <Kbd>Ejecutar supresión</Kbd> en la fila correspondiente
            </li>
            <li>
              Confirma en el diálogo. <strong>Acción irreversible.</strong>
            </li>
            <li>
              El sistema anonimiza los datos personales (nombre, email,
              teléfono, documento) pero <strong>conserva los radicados</strong>{" "}
              porque son documentos públicos con valor jurídico.
            </li>
            <li>
              Queda registro en Auditoría con tu nombre y la hora
            </li>
          </Ol>

          <div className="callout warn">
            <strong>Solo el admin_tenant puede ejecutar supresiones.</strong>{" "}
            Los supervisores ven la lista pero no pueden ejecutar.
          </div>
          <div className="callout tip">
            💡 Si dudas si proceder (representación no clara, datos en
            litigio), consulta primero con el área jurídica de la alcaldía.
            Documenta tu decisión.
          </div>
        </Section>

        {/* ---- 14. Mejores prácticas ---- */}
        <Section title="14 — Mejores prácticas">
          <Ul
            items={[
              <>
                <strong>Capacita a tus funcionarios.</strong> Agenda 30-45
                min con cada nuevo usuario antes de entregarle credenciales.
              </>,
              <>
                <strong>Mantén el KB vivo.</strong> Revisa cada trimestre que
                los artículos sigan siendo correctos (precios, horarios,
                requisitos cambian).
              </>,
              <>
                <strong>Monitorea SLA semanalmente.</strong> Si ves muchos
                vencidos, identifica qué dependencia y dale apoyo.
              </>,
              <>
                <strong>Lee la auditoría.</strong> Una mirada rápida cada
                lunes detecta problemas antes de que crezcan.
              </>,
              <>
                <strong>Usa modo &quot;Por dependencia&quot; con criterio.</strong>{" "}
                Activarlo en una alcaldía pequeña genera fricción innecesaria;
                en una grande es indispensable para privacidad.
              </>,
              <>
                <strong>No compartas tu contraseña</strong> — ni siquiera con
                tu equipo TIC. Si necesitan acceso, crea otro usuario.
              </>,
              <>
                <strong>Si vas a salir del cargo</strong>: crea tu reemplazo
                como <code>admin_tenant</code>, pásale la posta en una
                sesión de 1h, y pídele que te archive a ti. NUNCA quedes sin
                al menos un admin activo.
              </>,
              <>
                <strong>Encuestas cortas</strong>. Más preguntas = menos
                respuestas. 3 buenas preguntas valen más que 10 mediocres.
              </>,
              <>
                <strong>Plantillas en lenguaje ciudadano.</strong> Evita
                jergas jurídicas. El ciudadano debe entender qué le estás
                diciendo.
              </>,
              <>
                <strong>Reporta problemas a MiPueblo</strong> por el canal
                de soporte. Cuanta más info nos des (capturas, hora, qué
                hacías), más rápido resolvemos.
              </>,
            ]}
          />
        </Section>

        {/* ---- Cierre ---- */}
        <Section title="¿Y si necesito ayuda?">
          <p>
            Para temas operativos del día a día, este manual debería
            cubrirte. Si algo no está claro o encuentras un error:
          </p>
          <Ul
            items={[
              <>
                Cosas que <strong>NO puedes</strong> hacer desde tu panel
                (cambiar NIT, slug, plan contratado, agregar dependencias) →{" "}
                <strong>contacta a soporte MiPueblo</strong>
              </>,
              <>
                Problemas técnicos (página no carga, mensajes de error) →{" "}
                <strong>soporte MiPueblo</strong> con captura y hora
              </>,
              <>
                Dudas legales (cómo aplicar la Ley 1755 a un caso raro) →{" "}
                <strong>área jurídica</strong> de tu alcaldía
              </>,
              <>
                Capacitación adicional para tu equipo →{" "}
                <strong>soporte MiPueblo</strong> agenda sesiones
              </>,
            ]}
          />
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Suppress unused warning for Checklist helper (kept for consistency with sibling docs)
void Checklist;
